import type { ComponentType } from "react";

// Eager load critical pages (above-the-fold / home).
import Index from "./pages/Index";
import App from "./App";

/**
 * Route-level lazy helper for vite-react-ssg.
 *
 * IMPORTANT: do NOT use React.lazy() + `element` for these routes.
 * vite-react-ssg awaits each matched route's `lazy()` BEFORE hydrating
 * (and before SSG render), so the first client render matches the
 * server HTML. React.lazy in `element` is invisible to that mechanism,
 * which makes the client render the <Suspense> fallback on first paint
 * while the server sent the full page — causing hydration errors
 * (#418 / #422 / #425). React Router resolves `lazy` -> `Component`.
 */
const page =
  (factory: () => Promise<{ default: ComponentType }>) =>
  () =>
    factory().then((m) => ({ Component: m.default }));

export const routes = [
  {
    path: "/",
    element: <App />,
    children: [
      { index: true, element: <Index /> },
      { path: "about", lazy: page(() => import("./pages/About")) },
      { path: "contact", lazy: page(() => import("./pages/Contact")) },
      { path: "case-studies", lazy: page(() => import("./pages/CaseStudies")) },
      { path: "case-studies/:slug", lazy: page(() => import("./pages/CaseStudyDetail")) },
      { path: "services", lazy: page(() => import("./pages/Services")) },
      { path: "services/enterprise-mobility-solutions", lazy: page(() => import("./pages/EnterpriseMobility")) },
      { path: "services/mvp-development", lazy: page(() => import("./pages/MVPDevelopment")) },
      { path: "services/cloud-cost-optimisation", lazy: page(() => import("./pages/CloudCostOptimisation")) },
      { path: "services/flutter-development", lazy: page(() => import("./pages/FlutterDevelopment")) },
      { path: "services/react-native-development", lazy: page(() => import("./pages/ReactNativeDevelopment")) },
      { path: "services/android-app-development", lazy: page(() => import("./pages/AndroidDevelopment")) },
      { path: "services/ios-app-development", lazy: page(() => import("./pages/IOSDevelopment")) },
      { path: "services/cross-platform-app-development", lazy: page(() => import("./pages/CrossPlatformDevelopment")) },
      { path: "services/white-label-app-development", lazy: page(() => import("./pages/WhiteLabelAppDevelopment")) },
      { path: "services/native-app-development", lazy: page(() => import("./pages/NativeAppDevelopment")) },
      { path: "services/application-modernization", lazy: page(() => import("./pages/ApplicationModernization")) },
      { path: "services/data-engineering", lazy: page(() => import("./pages/DataEngineering")) },
      { path: "services/staff-augmentation", lazy: page(() => import("./pages/StaffAugmentation")) },
      { path: "services/saas-development", lazy: page(() => import("./pages/SaaSDevelopment")) },
      { path: "services/ai-agent-development", lazy: page(() => import("./pages/AIAgentDevelopment")) },
      { path: "services/llm-integration", lazy: page(() => import("./pages/LLMIntegration")) },
      { path: "services/hipaa-compliant-development", lazy: page(() => import("./pages/HIPAACompliantDevelopment")) },
      { path: "services/nextjs-development", lazy: page(() => import("./pages/NextJSDevelopment")) },
      { path: "services/dedicated-development-team", lazy: page(() => import("./pages/DedicatedDevelopmentTeam")) },
      { path: "services/outsource-software-development", lazy: page(() => import("./pages/OutsourceSoftwareDevelopment")) },
      { path: "services/enterprise-software-development", lazy: page(() => import("./pages/EnterpriseSoftwareDevelopment")) },
      { path: "services/telehealth-app-development", lazy: page(() => import("./pages/TelehealthAppDevelopment")) },
      { path: "services/hire-ai-developers", lazy: page(() => import("./pages/HireAIDevelopers")) },
      { path: "services/answer-engine-optimization", lazy: page(() => import("./pages/AnswerEngineOptimization")) },
      { path: "services/:slug", lazy: page(() => import("./pages/ServiceDetail")) },
      { path: "industries", lazy: page(() => import("./pages/Industries")) },
      { path: "industries/:slug", lazy: page(() => import("./pages/IndustryDetail")) },
      { path: "blog", lazy: page(() => import("./pages/Blog")) },
      { path: "blog/author/:slug", lazy: page(() => import("./pages/AuthorPage")) },
      { path: "blog/:slug", lazy: page(() => import("./pages/BlogDetail")) },
      { path: "testimonials", lazy: page(() => import("./pages/Testimonials")) },
      { path: "privacy/", lazy: page(() => import("./pages/PrivacyPolicy")) },
      { path: "terms/", lazy: page(() => import("./pages/TermsOfService")) },
      { path: "careers", lazy: page(() => import("./pages/Careers")) },
      { path: "faqs", lazy: page(() => import("./pages/FAQs")) },
      { path: "portfolio", lazy: page(() => import("./pages/Portfolio")) },
      { path: "roi-calculator", lazy: page(() => import("./pages/ROICalculatorPage")) },
      { path: "pricing", lazy: page(() => import("./pages/Pricing")) },
      { path: "locations/:slug", lazy: page(() => import("./pages/LocationPage")) },
      { path: "about-our-delivery-model", lazy: page(() => import("./pages/DeliveryModel")) },
      { path: "compliance", lazy: page(() => import("./pages/Compliance")) },
      { path: "regions/middle-east", lazy: page(() => import("./pages/MiddleEast")) },
      { path: "regions/australia", lazy: page(() => import("./pages/Australia")) },
      { path: "regions/uk", lazy: page(() => import("./pages/UK")) },
      { path: "locations", lazy: page(() => import("./pages/Locations")) },
      { path: "products", lazy: page(() => import("./pages/Products")) },
      { path: "products/fleet-sync-pro", lazy: page(() => import("./pages/products/FleetSyncPro")) },
      { path: "products/azimuth", lazy: page(() => import("./pages/products/Azimuth")) },
      { path: "products/residenta", lazy: page(() => import("./pages/products/Residenta")) },
      { path: "products/smart-retail-engine", lazy: page(() => import("./pages/products/SmartRetail")) },
      { path: "products/skill-gauge", lazy: page(() => import("./pages/products/SkillGauge")) },
      { path: "products/gymapp", lazy: page(() => import("./pages/products/GymApp")) },
      { path: "why-ortem", lazy: page(() => import("./pages/WhyOrtem")) },
      { path: "our-methodology", lazy: page(() => import("./pages/OurMethodology")) },
      { path: "innovation-lab", lazy: page(() => import("./pages/InnovationLab")) },
      { path: "tools/app-cost-calculator", lazy: page(() => import("./pages/AppCostCalculator")) },
      { path: "tools/ai-cost-calculator", lazy: page(() => import("./pages/AICostCalculator")) },
      { path: "reports/software-development-cost-2026", lazy: page(() => import("./pages/CostReport2026")) },
      { path: "vs/bairesdev", lazy: page(() => import("./pages/vs/BairesDev")) },
      { path: "vs/intellectsoft", lazy: page(() => import("./pages/vs/Intellectsoft")) },
      { path: "vs/toptal", lazy: page(() => import("./pages/vs/Toptal")) },
      { path: "vs/appinventiv", lazy: page(() => import("./pages/vs/Appinventiv")) },
      { path: "vs/simform", lazy: page(() => import("./pages/vs/Simform")) },
      { path: "vs/solulab", lazy: page(() => import("./pages/vs/SoluLab")) },
      { path: "vs/turing", lazy: page(() => import("./pages/vs/Turing")) },
      { path: "vs/andela", lazy: page(() => import("./pages/vs/Andela")) },
      { path: "*", lazy: page(() => import("./pages/NotFound")) },
    ],
  },
];
